import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  Permission,
  type AccessRightWithPermissionsDto,
} from "Infrastructure/Api/Api";

const EMPTY_ACCESS_RIGHTS: Array<AccessRightWithPermissionsDto> = [];

/**
 * Hook to access the user's access rights with permission flags
 */
export const useAccessRightsWithPermissions =
  (): Array<AccessRightWithPermissionsDto> =>
    useLoggedInUser()?.accessRights ?? EMPTY_ACCESS_RIGHTS;

export const PERMISSION_TO_FIELD_NAME: Record<
  Permission,
  keyof AccessRightWithPermissionsDto | undefined
> = {
  [Permission.Read]: "canRead",
  [Permission.Create]: "canCreate",
  [Permission.Update]: "canUpdate",
  [Permission.Delete]: "canDelete",
  [Permission.Export]: "canExport",
  [Permission.None]: undefined,
  [Permission.All]: undefined,
};

const ALL_PERMISSION_FIELDS: Array<keyof AccessRightWithPermissionsDto> = [
  "canRead",
  "canCreate",
  "canUpdate",
  "canDelete",
  "canExport",
];

export const hasPermission = (
  accessRight: AccessRightWithPermissionsDto | undefined,
  permission: Permission,
): boolean => {
  if (!accessRight) {
    return false;
  }

  if (permission === Permission.All) {
    return ALL_PERMISSION_FIELDS.every((field) => accessRight[field]);
  }

  const field = PERMISSION_TO_FIELD_NAME[permission];
  if (!field) {
    return false;
  }

  return !!accessRight[field];
};

/**
 * Hook that checks if the current user has a specific permission for an access right.
 * Returns a boolean directly.
 */
export const useCheckPermission = (
  accessRightCode: string,
  permission: Permission,
): boolean => {
  const accessRights = useAccessRightsWithPermissions();
  const accessRight = accessRights.find(({ code }) => code === accessRightCode);

  return hasPermission(accessRight, permission);
};
